import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const { isDarkMode, toggleTheme } = useTheme();
  const { userProfile, signOut } = useAuth();

  const userRole = userProfile?.role || 'user';

  const userMenuItems = [
    { label: 'Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Points Verification', path: '/points-verification', icon: 'ShieldCheck' },
    { label: 'Analytics', path: '/analytics-dashboard', icon: 'BarChart3' },
    { label: 'Notifications', path: '/notifications', icon: 'Bell' }
  ];

  const adminMenuItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'LayoutDashboard' },
    { label: 'Manage Tournaments', path: '/tournaments-list', icon: 'Settings' },
    { label: 'Team Management', path: '/global-team-management', icon: 'Shield' },
    { label: 'Squad Management', path: '/tournament-squad-management', icon: 'Users' },
    { label: 'Results', path: '/results-management', icon: 'CheckCircle' },
    { label: 'Winner Announcement', path: '/winner-announcement', icon: 'Trophy' }
  ];

  const menuItems = userRole === 'admin' ? adminMenuItems : userMenuItems;

  const handleSignOut = async () => {
    await signOut();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => {
    return location?.pathname === path;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(event?.target)) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="nav-header">
        <div className="nav-header-content">
          <Link to={userRole === 'admin' ? '/admin-dashboard' : '/user-dashboard'} className="nav-header-logo">
            <div className="nav-header-logo-icon">
              <Icon name="Target" size={24} color="#FFFFFF" />
            </div>
            <span className="nav-header-logo-text">PowerPlay</span>
          </Link>

          <nav className="nav-header-menu">
            {menuItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`nav-header-menu-item ${isActive(item?.path) ? 'active' : ''}`}
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          <div className="nav-header-actions">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-250"
              aria-label="Toggle theme"
            >
              <Icon name={isDarkMode ? 'Sun' : 'Moon'} size={20} />
            </button>
            <button
              onClick={handleSignOut}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-250"
              aria-label="Sign out"
              title="Sign out"
            >
              <Icon name="LogOut" size={20} />
            </button>
            <button
              onClick={toggleMobileMenu}
              className="nav-header-mobile-toggle"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      <div
        ref={mobileMenuRef}
        className={`nav-mobile-menu ${!isMobileMenuOpen ? 'closed' : ''}`}
      >
        <nav className="nav-mobile-menu-content">
          {menuItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`nav-mobile-menu-item ${isActive(item?.path) ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <Icon name={item?.icon} size={20} className="inline-block mr-3" />
              {item?.label}
            </Link>
          ))}
        </nav>
      </div>
    </>
  );
};

export default Header;